import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  BookOpen, Clock, CheckCircle, XCircle, ChevronLeft, 
  ChevronRight, RotateCcw, Target, Award, Lightbulb
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const PracticeTestPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Test state
  const [testState, setTestState] = useState('setup'); // setup, test, results
  const [loading, setLoading] = useState(false);
  
  // Setup options
  const [questionCount, setQuestionCount] = useState(25);
  
  // Test data
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showExplanation, setShowExplanation] = useState(false);
  const [hasAnswered, setHasAnswered] = useState(false);
  
  // Results
  const [results, setResults] = useState({ correct: 0, total: 0 });

  const getAuthHeaders = () => {
    const token = sessionStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  const startPracticeTest = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/quizzes/practice-test?question_count=${questionCount}`, {
        headers: getAuthHeaders()
      });
      setQuestions(response.data.questions);
      setTestState('test');
      setCurrentQuestionIndex(0);
      setAnswers({});
      setResults({ correct: 0, total: 0 });
    } catch (error) {
      console.error('Failed to load practice test:', error);
      toast.error('Failed to load practice test');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerSelect = (answer) => {
    if (hasAnswered) return;
    
    const currentQuestion = questions[currentQuestionIndex];
    const isCorrect = answer === currentQuestion.correct_answer;
    
    setAnswers(prev => ({
      ...prev,
      [currentQuestion.id]: {
        selected: answer,
        correct: currentQuestion.correct_answer,
        isCorrect
      }
    }));
    
    setResults(prev => ({
      correct: prev.correct + (isCorrect ? 1 : 0),
      total: prev.total + 1
    }));
    
    setHasAnswered(true);
    setShowExplanation(true);
  };

  const goToNextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
      setHasAnswered(false);
      setShowExplanation(false);
    } else {
      setTestState('results');
    }
  };

  const restartTest = () => {
    setTestState('setup');
    setQuestions([]);
    setCurrentQuestionIndex(0);
    setAnswers({});
    setHasAnswered(false);
    setShowExplanation(false);
    setResults({ correct: 0, total: 0 });
  };

  // Setup Screen
  if (testState === 'setup') {
    return (
      <div className="min-h-screen bg-background">
        <header className="border-b border-border bg-card">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard">
              <ChevronLeft className="w-5 h-5 mr-2" />
              Back to Dashboard
            </Button>
          </div>
        </header>

        <main className="max-w-2xl mx-auto px-6 py-12">
          <div className="bg-card border border-border rounded-sm p-8">
            <div className="text-center mb-8">
              <Target className="w-16 h-16 text-primary mx-auto mb-4" />
              <h1 className="text-3xl font-black mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
                PRACTICE TEST
              </h1>
              <p className="text-muted-foreground">
                Test your knowledge with questions from the chapter quizzes
              </p>
            </div>

            <div className="bg-primary/5 border border-primary/20 rounded-sm p-4 mb-8">
              <div className="flex items-start gap-3">
                <Lightbulb className="w-5 h-5 text-primary mt-0.5" />
                <div>
                  <h3 className="font-semibold text-primary mb-1">How It Works</h3>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>• Questions are randomly selected from chapter quizzes</li>
                    <li>• Get immediate feedback after each answer</li>
                    <li>• See detailed explanations for every question</li>
                    <li>• Take as many practice tests as you need</li>
                    <li>• Final exam questions are NOT included (preserved for the real exam)</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="mb-8">
              <label className="block text-sm font-medium mb-3">Number of Questions</label>
              <div className="grid grid-cols-3 gap-3">
                {[25, 50, 90].map((count) => (
                  <button
                    key={count}
                    onClick={() => setQuestionCount(count)}
                    className={`p-4 rounded-sm border transition-all ${
                      questionCount === count 
                        ? 'border-primary bg-primary/10 text-primary' 
                        : 'border-border hover:border-primary/50'
                    }`}
                    data-testid={`question-count-${count}`}
                  >
                    <div className="text-2xl font-black" style={{ fontFamily: 'Barlow Condensed' }}>
                      {count}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {count === 25 ? 'Quick' : count === 50 ? 'Standard' : 'Full'}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <Button 
              className="w-full" 
              size="lg"
              onClick={startPracticeTest}
              disabled={loading}
              data-testid="start-practice-test-btn"
            >
              {loading ? 'Loading...' : 'Start Practice Test'}
            </Button>
          </div>
        </main>
      </div>
    );
  }

  // Test Screen
  if (testState === 'test') {
    const currentQuestion = questions[currentQuestionIndex];
    const progress = ((currentQuestionIndex + 1) / questions.length) * 100;
    const currentAnswer = answers[currentQuestion?.id];

    return (
      <div className="min-h-screen bg-background">
        {/* Header */}
        <header className="border-b border-border bg-card sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-6 py-3 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Target className="w-6 h-6 text-primary" />
              <span className="font-bold" style={{ fontFamily: 'Barlow Condensed' }}>
                PRACTICE TEST
              </span>
            </div>
            
            <div className="flex items-center gap-6">
              <div className="text-sm">
                Question <strong>{currentQuestionIndex + 1}</strong> of <strong>{questions.length}</strong>
              </div>
              <div className="text-sm">
                Score: <span className="text-green-500 font-bold">{results.correct}</span>
                <span className="text-muted-foreground">/{results.total}</span>
              </div>
            </div>
          </div>
          <Progress value={progress} className="h-1" />
        </header>

        {/* Question Content */}
        <main className="pt-8 pb-32 px-6">
          <div className="max-w-3xl mx-auto">
            {currentQuestion && (
              <div className="bg-card border border-border rounded-sm p-8">
                {currentQuestion.source_chapter && (
                  <div className="mb-2 text-xs text-muted-foreground">
                    From Chapter {currentQuestion.source_chapter}
                  </div>
                )}
                
                <h2 className="text-xl font-semibold mb-6 leading-relaxed">
                  {currentQuestion.question_text}
                </h2>

                <div className="space-y-3">
                  {currentQuestion.options.map((option, index) => {
                    const letter = String.fromCharCode(65 + index);
                    const isSelected = currentAnswer?.selected === letter;
                    const isCorrect = letter === currentQuestion.correct_answer;
                    const showResult = hasAnswered;
                    
                    let bgClass = 'border-border hover:border-primary/50 hover:bg-secondary/50';
                    if (showResult) {
                      if (isCorrect) {
                        bgClass = 'border-green-500 bg-green-500/10';
                      } else if (isSelected && !currentAnswer?.isCorrect) {
                        bgClass = 'border-red-500 bg-red-500/10';
                      } else {
                        bgClass = 'border-border opacity-50';
                      }
                    } else if (isSelected) {
                      bgClass = 'border-primary bg-primary/10';
                    }
                    
                    return (
                      <button
                        key={index}
                        onClick={() => handleAnswerSelect(letter)}
                        disabled={hasAnswered}
                        className={`w-full text-left p-4 rounded-sm border transition-all ${bgClass}`}
                        data-testid={`answer-option-${letter}`}
                      >
                        <div className="flex items-start gap-3">
                          <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                            showResult && isCorrect ? 'bg-green-500 text-white' :
                            showResult && isSelected && !currentAnswer?.isCorrect ? 'bg-red-500 text-white' :
                            isSelected ? 'bg-primary text-primary-foreground' : 'bg-secondary'
                          }`}>
                            {showResult && isCorrect ? <CheckCircle className="w-5 h-5" /> :
                             showResult && isSelected && !currentAnswer?.isCorrect ? <XCircle className="w-5 h-5" /> :
                             letter}
                          </span>
                          <span className="flex-1 pt-1">{option}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>

                {/* Explanation */}
                {showExplanation && currentQuestion.explanation && (
                  <div className={`mt-6 p-4 rounded-sm border ${
                    currentAnswer?.isCorrect ? 'bg-green-500/5 border-green-500/30' : 'bg-accent/5 border-accent/30'
                  }`}>
                    <div className="flex items-start gap-3">
                      <Lightbulb className={`w-5 h-5 mt-0.5 ${currentAnswer?.isCorrect ? 'text-green-500' : 'text-accent'}`} />
                      <div>
                        <h4 className="font-semibold mb-1">
                          {currentAnswer?.isCorrect ? 'Correct!' : 'Explanation'}
                        </h4>
                        <p className="text-sm text-muted-foreground">
                          {currentQuestion.explanation}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </main>

        {/* Fixed Footer */}
        <footer className="border-t border-border bg-card fixed bottom-0 left-0 right-0">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
            <Button variant="outline" onClick={restartTest}>
              <RotateCcw className="w-4 h-4 mr-2" />
              Restart
            </Button>
            
            <Button 
              onClick={goToNextQuestion}
              disabled={!hasAnswered}
              data-testid="next-question-btn"
            >
              {currentQuestionIndex === questions.length - 1 ? 'See Results' : 'Next Question'}
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </footer>
      </div>
    );
  }

  // Results Screen
  if (testState === 'results') {
    const percentage = Math.round((results.correct / results.total) * 100);
    const passed = percentage >= 75;

    return (
      <div className="min-h-screen bg-background">
        <header className="border-b border-border bg-card">
          <div className="max-w-4xl mx-auto px-6 py-4">
            <h1 className="text-xl font-bold" style={{ fontFamily: 'Barlow Condensed' }}>
              PRACTICE TEST RESULTS
            </h1>
          </div>
        </header>

        <main className="max-w-2xl mx-auto px-6 py-12">
          <div className="bg-card border border-border rounded-sm p-8 text-center">
            {passed ? (
              <>
                <Award className="w-20 h-20 text-green-500 mx-auto mb-4" />
                <h2 className="text-3xl font-black text-green-500 mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
                  GREAT JOB!
                </h2>
                <p className="text-xl mb-6">You're on track for the final exam!</p>
              </>
            ) : (
              <>
                <BookOpen className="w-20 h-20 text-accent mx-auto mb-4" />
                <h2 className="text-3xl font-black text-accent mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
                  KEEP PRACTICING
                </h2>
                <p className="text-xl mb-6">Review the chapters and try again!</p>
              </>
            )}

            <div className="grid grid-cols-3 gap-4 mb-8">
              <div className="bg-secondary rounded-sm p-4">
                <div className="text-3xl font-black text-green-500" style={{ fontFamily: 'Barlow Condensed' }}>
                  {results.correct}
                </div>
                <div className="text-sm text-muted-foreground">Correct</div>
              </div>
              <div className="bg-secondary rounded-sm p-4">
                <div className="text-3xl font-black" style={{ fontFamily: 'Barlow Condensed' }}>
                  {results.total}
                </div>
                <div className="text-sm text-muted-foreground">Total</div>
              </div>
              <div className="bg-secondary rounded-sm p-4">
                <div className={`text-3xl font-black ${passed ? 'text-green-500' : 'text-accent'}`} style={{ fontFamily: 'Barlow Condensed' }}>
                  {percentage}%
                </div>
                <div className="text-sm text-muted-foreground">Score</div>
              </div>
            </div>

            <div className="text-sm text-muted-foreground mb-6">
              Final Exam Passing Score: 75%
            </div>

            <div className="flex gap-4 justify-center">
              <Button variant="outline" onClick={restartTest} data-testid="try-again-btn">
                <RotateCcw className="w-4 h-4 mr-2" />
                Practice Again
              </Button>
              <Button onClick={() => navigate('/dashboard')} data-testid="return-dashboard-btn">
                Return to Dashboard
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return null;
};

export default PracticeTestPage;
